import type { WsEvent } from '@hotel-manager/shared-types';
import { useEffect, useRef, useState } from 'react';
import { type WsClient, type WsStatus, createWsClient, defaultWsUrl } from '../api/ws.js';

/**
 * HM-071 — Hook che monta una singola connessione WS per Dashboard.
 * Espone status + subscribe API.
 */
export type UseWsConnection = {
  status: WsStatus;
  client: WsClient | null;
};

export function useWsConnection(): UseWsConnection {
  const [status, setStatus] = useState<WsStatus>('connecting');
  const clientRef = useRef<WsClient | null>(null);

  useEffect(() => {
    if (typeof globalThis.WebSocket === 'undefined') {
      setStatus('closed');
      return;
    }
    const client = createWsClient({ url: defaultWsUrl() });
    clientRef.current = client;
    const off = client.onStatus(setStatus);
    return () => {
      off();
      client.close();
      clientRef.current = null;
    };
  }, []);

  return { status, client: clientRef.current };
}

/** HM-072 — subscribe handler tipizzato. */
export function useWsEvents(client: WsClient | null, handler: (ev: WsEvent) => void): void {
  useEffect(() => {
    if (!client) return;
    const off = client.on(handler);
    return () => {
      off();
    };
  }, [client, handler]);
}
